import { useMemo, useRef, useEffect } from 'react';
import SessionHandler from '@sendbird/uikit-react/handlers/SessionHandler';
import useSendbirdToken from '@/shared/hooks/useSendbirdToken';
import SentryService from '@/services/sentry/Sentry.service';
import {
  createTokenRequestWithBackoff,
  createTokenManagerState,
} from '@/shared/utils/chatUtils';

export function useSessionHandler() {
  const { fetchSendbirdSessionToken } = useSendbirdToken();
  const tokenManagerState = useRef(createTokenManagerState());

  // Use ref to store latest fetch function to avoid recreating handler
  const fetchTokenRef = useRef(fetchSendbirdSessionToken);

  // Update ref when fetch function changes
  useEffect(() => {
    fetchTokenRef.current = fetchSendbirdSessionToken;
  }, [fetchSendbirdSessionToken]);

  // Create handler only once - use ref to always call latest fetch function
  const sessionHandler = useMemo(() => {
    const handler = new SessionHandler();

    handler.onSessionTokenRequired = createTokenRequestWithBackoff(
      () => fetchTokenRef.current(),
      tokenManagerState.current,
    );

    handler.onSessionError = (error) => {
      SentryService.captureError(
        'Sendbird session handler: session error',
        error,
      );
    };

    return handler;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only create once

  return sessionHandler;
}

export default useSessionHandler;
